import React, { useEffect, useMemo, useState } from "react";
import { Plus, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { WORLDS } from "@/lib/worlds";
import { providerApi, SLOT_MODULES } from "@/lib/providerApi";

const TITLE_MAX = 60;
const SUBTITLE_MAX = 40;

export const emptyListing = (module = "") => ({
  module,
  category: "",
  title: "",
  subtitle: "",
  description: "",
  price: "",
  location: "",
  images: [],
  services: [],
  accepts_cash: false,
  mobile: false,
  provider_kind: "venue",
  teaches_under_18: false,
});

/** Turn a stored listing into form state. */
export function listingToForm(l) {
  return {
    ...emptyListing(l.module),
    category: l.category || "",
    title: l.title || "",
    subtitle: l.subtitle || "",
    description: l.description || "",
    price: l.price != null ? String(l.price) : "",
    location: l.location || "",
    images: l.images?.length ? l.images : l.image_url ? [l.image_url] : [],
    services: (l.services || []).map((s) => ({ id: s.id, name: s.name || "", price: s.price != null ? String(s.price) : "", duration: s.duration != null ? String(s.duration) : "" })),
    accepts_cash: !!l.accepts_cash,
    mobile: !!l.mobile,
    provider_kind: l.provider_kind || "venue",
    teaches_under_18: !!l.teaches_under_18,
  };
}

/** Form state → API body (ListingCreate / ListingPatch). */
export function formToBody(f, { create = true } = {}) {
  const services = f.services
    .filter((s) => s.name.trim())
    .map((s) => ({ ...(s.id ? { id: s.id } : {}), name: s.name.trim(), price: Number(s.price) || 0, duration: s.duration ? Number(s.duration) : null }));
  const images = f.images.map((u) => u.trim()).filter(Boolean);
  const body = {
    title: f.title.trim(),
    subtitle: f.subtitle.trim(),
    description: f.description.trim(),
    price: Number(f.price) || (services[0]?.price ?? 0),
    location: f.location.trim(),
    images,
    image_url: images[0] || undefined,
    services,
    accepts_cash: !!f.accepts_cash,
    mobile: !!f.mobile,
    provider_kind: f.provider_kind,
    teaches_under_18: !!f.teaches_under_18,
    category: f.category,
  };
  if (create) body.module = f.module;
  return body;
}

export function validateListing(f) {
  if (!f.module) return "Pick a world.";
  if (!f.category) return "Pick a category.";
  if (f.title.trim().length < 2) return "Give the listing a title.";
  if (f.title.trim().length > TITLE_MAX) return `Titles are up to ${TITLE_MAX} characters.`;
  if (f.subtitle.trim().length > SUBTITLE_MAX) return `Subtitles are up to ${SUBTITLE_MAX} characters.`;
  const named = f.services.filter((s) => s.name.trim() && Number(s.price) > 0);
  if (!(Number(f.price) > 0) && !named.length) return "Add a price (or at least one priced service).";
  return null;
}

export function useModules() {
  const [mods, setMods] = useState(null);
  useEffect(() => {
    providerApi.modules().then(setMods).catch(() => setMods([]));
  }, []);
  return mods;
}

const field = "w-full rounded-xl border border-border bg-background px-3 py-2.5 text-sm outline-none focus:border-primary";

export function ListingForm({ value, onChange, mode = "create", mods }) {
  const f = value;
  const set = (patch) => onChange({ ...f, ...patch });
  const loaded = useModules();
  const modules = mods || loaded || [];
  const mod = modules.find((m) => m.key === f.module);
  const cats = mod?.categories || [];
  const slot = SLOT_MODULES.includes(f.module);
  const ui = useMemo(() => WORLDS.find((w) => w.key === f.module), [f.module]);

  return (
    <div className="space-y-6">
      {mode === "create" && (
        <div>
          <Label>World</Label>
          <div className="mt-2 grid grid-cols-2 gap-2 sm:grid-cols-3">
            {WORLDS.map((w) => {
              const Icon = w.icon;
              const on = w.key === f.module;
              return (
                <button key={w.key} type="button" data-testid={`lf-world-${w.key}`} onClick={() => set({ module: w.key, category: "" })} className={`flex items-center gap-2 rounded-xl border px-3 py-2.5 text-left text-sm font-semibold ${on ? "border-primary bg-primary/5" : "border-border hover:bg-muted"}`}>
                  <Icon size={16} style={{ color: w.accent }} /> {w.name}
                </button>
              );
            })}
          </div>
        </div>
      )}

      <div>
        <Label htmlFor="lf-category">Category</Label>
        <select id="lf-category" data-testid="lf-category" value={f.category} onChange={(e) => set({ category: e.target.value })} className={`${field} mt-1.5`} disabled={!f.module}>
          <option value="">{f.module ? "Choose a category" : "Pick a world first"}</option>
          {cats.filter((c) => c.key !== "other").map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
          {cats.some((c) => c.key === "other") && <option value="other">Other</option>}
        </select>
      </div>

      <div className="grid gap-4 sm:grid-cols-2">
        <div>
          <Label htmlFor="lf-title">Title <span className="text-muted-foreground">({f.title.length}/{TITLE_MAX})</span></Label>
          <Input id="lf-title" data-testid="lf-title" value={f.title} maxLength={TITLE_MAX} onChange={(e) => set({ title: e.target.value })} placeholder={ui ? `e.g. ${ui.searchPlaceholder?.replace("?", "") || "Skin fade & beard"}` : "e.g. Skin fade & beard"} className="mt-1.5" />
        </div>
        <div>
          <Label htmlFor="lf-subtitle">Subtitle <span className="text-muted-foreground">({f.subtitle.length}/{SUBTITLE_MAX})</span></Label>
          <Input id="lf-subtitle" data-testid="lf-subtitle" value={f.subtitle} maxLength={SUBTITLE_MAX} onChange={(e) => set({ subtitle: e.target.value })} placeholder="One line customers see on the card" className="mt-1.5" />
        </div>
      </div>

      <div>
        <Label htmlFor="lf-description">Description</Label>
        <Textarea id="lf-description" data-testid="lf-description" value={f.description} onChange={(e) => set({ description: e.target.value })} rows={5} placeholder="What you offer, what's included, anything customers should know." className="mt-1.5" />
      </div>

      <div className="grid gap-4 sm:grid-cols-2">
        <div>
          <Label htmlFor="lf-price">{slot && f.services.length ? "Starting price (£)" : f.module === "rentals" ? "Price per day (£)" : "Price (£)"}</Label>
          <Input id="lf-price" data-testid="lf-price" inputMode="decimal" value={f.price} onChange={(e) => set({ price: e.target.value.replace(/[^0-9.]/g, "") })} placeholder="25" className="mt-1.5" />
        </div>
        <div>
          <Label htmlFor="lf-location">Location</Label>
          <Input id="lf-location" data-testid="lf-location" value={f.location} onChange={(e) => set({ location: e.target.value })} placeholder="Town, area or postcode" className="mt-1.5" />
        </div>
      </div>

      {slot && (
        <div>
          <div className="flex items-center justify-between">
            <Label>Services & prices</Label>
            <Button type="button" size="sm" variant="outline" className="rounded-full" data-testid="lf-add-service" onClick={() => set({ services: [...f.services, { name: "", price: "", duration: "60" }] })}><Plus size={14} className="mr-1" /> Add service</Button>
          </div>
          <p className="mt-1 text-xs text-muted-foreground">Each service gets its own price and length; customers pick one when they book.</p>
          <div className="mt-2 space-y-2">
            {f.services.map((s, i) => (
              <div key={i} className="grid grid-cols-[1fr_90px_90px_36px] gap-2">
                <Input value={s.name} placeholder="Service name" data-testid={`lf-service-name-${i}`} onChange={(e) => set({ services: f.services.map((x, j) => (j === i ? { ...x, name: e.target.value } : x)) })} />
                <Input value={s.price} placeholder="£" inputMode="decimal" data-testid={`lf-service-price-${i}`} onChange={(e) => set({ services: f.services.map((x, j) => (j === i ? { ...x, price: e.target.value.replace(/[^0-9.]/g, "") } : x)) })} />
                <Input value={s.duration} placeholder="mins" inputMode="numeric" onChange={(e) => set({ services: f.services.map((x, j) => (j === i ? { ...x, duration: e.target.value.replace(/[^0-9]/g, "") } : x)) })} />
                <button type="button" aria-label="Remove service" className="rounded-xl border border-border text-muted-foreground hover:text-destructive" onClick={() => set({ services: f.services.filter((_, j) => j !== i) })}><Trash2 size={15} className="mx-auto" /></button>
              </div>
            ))}
          </div>
        </div>
      )}

      <div>
        <div className="flex items-center justify-between">
          <Label>Photos</Label>
          <Button type="button" size="sm" variant="outline" className="rounded-full" onClick={() => set({ images: [...f.images, ""] })}><Plus size={14} className="mr-1" /> Add photo link</Button>
        </div>
        <p className="mt-1 text-xs text-muted-foreground">Paste image links for now (the app can upload from your camera roll). The first one is the cover.</p>
        <div className="mt-2 space-y-2">
          {f.images.map((u, i) => (
            <div key={i} className="flex gap-2">
              <Input value={u} placeholder="https://…" data-testid={`lf-image-${i}`} onChange={(e) => set({ images: f.images.map((x, j) => (j === i ? e.target.value : x)) })} />
              <button type="button" aria-label="Remove photo" className="rounded-xl border border-border px-3 text-muted-foreground hover:text-destructive" onClick={() => set({ images: f.images.filter((_, j) => j !== i) })}><Trash2 size={15} /></button>
            </div>
          ))}
        </div>
      </div>

      <div className="grid gap-3 sm:grid-cols-2">
        <label className="flex items-start gap-3 rounded-xl border border-border p-3 text-sm">
          <input type="checkbox" className="mt-1" checked={f.accepts_cash} onChange={(e) => set({ accepts_cash: e.target.checked })} data-testid="lf-cash" />
          <span><b>Accept cash on the day</b><br /><span className="text-muted-foreground">Customers can book without paying online and pay you in person.</span></span>
        </label>
        {(slot || f.module === "food") && (
          <label className="flex items-start gap-3 rounded-xl border border-border p-3 text-sm">
            <input type="checkbox" className="mt-1" checked={f.mobile} onChange={(e) => set({ mobile: e.target.checked, provider_kind: e.target.checked ? "mobile" : "venue" })} data-testid="lf-mobile" />
            <span><b>I travel to the customer</b><br /><span className="text-muted-foreground">Mobile / at-home service.</span></span>
          </label>
        )}
        {f.module === "education" && (
          <label className="flex items-start gap-3 rounded-xl border border-border p-3 text-sm">
            <input type="checkbox" className="mt-1" checked={f.teaches_under_18} onChange={(e) => set({ teaches_under_18: e.target.checked })} />
            <span><b>I teach learners under 18</b><br /><span className="text-muted-foreground">Shows parents the safeguarding information they need.</span></span>
          </label>
        )}
      </div>
    </div>
  );
}
